// ProfessionalExperienceEdit.jsx
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  SectionTitle,
} from 'modules/shared/components';
import {
  DATE_ERROR_MESSAGES,
  EMPLOYMENT_TYPE_OPTIONS,
  EMPLOYMENT_TYPES,
  EMPTY_EXPERIENCE,
  EXPERIENCE_LIMITS,
  MONTH_YEAR_VARIANT,
  MONTHS,
  WORK_TYPE_OPTIONS,
  WORK_TYPES,
} from '../constants';
import { formatExperienceDuration, isEndAfterStart, isFutureMonthYear } from '../utils';

export default function ProfessionalExperienceEdit({ initialSavedCount = 0, onEditStateChange }) {
  const { control, trigger, reset, resetField } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'professionalExperience',
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [savedCount, setSavedCount] = useState(initialSavedCount);

  const isEditorOpen = editingIndex !== null;
  const isNewEntry = editingIndex === fields.length;

  useEffect(() => {
    if (fields.length === 0 && editingIndex === null) {
      append({ ...EMPTY_EXPERIENCE });
      setEditingIndex(0);
    }
  }, [fields.length, editingIndex, append]);

  useEffect(() => {
    onEditStateChange?.(isEditorOpen);
  }, [isEditorOpen, onEditStateChange]);

  const handleAdd = () => {
    if (isEditorOpen) return;
    append({ ...EMPTY_EXPERIENCE });
    setEditingIndex(fields.length);
  };

  const handleEdit = (index) => {
    if (isEditorOpen) return;
    setEditingIndex(index);
  };

  const handleSave = async () => {
    const path = `professionalExperience.${editingIndex}`;
    const valid = await trigger(path, { shouldFocus: true });
    if (!valid) return;

    if (editingIndex === savedCount) {
      setSavedCount((c) => c + 1);
    }
    setEditingIndex(null);
  };

  const handleCancel = () => {
    if (savedCount === 0) {
      reset({ professionalExperience: [{ ...EMPTY_EXPERIENCE }] }, { keepDirty: false });
      setEditingIndex(0);
      return;
    }

    if (isNewEntry) {
      remove(fields.length - 1);
    } else {
      resetField(`professionalExperience.${editingIndex}`);
    }

    setEditingIndex(null);
  };

  const handleRemove = (index) => {
    if (isEditorOpen) return;

    if (fields.length === 1) {
      reset({ professionalExperience: [{ ...EMPTY_EXPERIENCE }] }, { keepDirty: false });
      setEditingIndex(0);
      return;
    }

    remove(index);
  };

  return (
    <div className='column gap-3'>
      <SectionTitle
        title='Professional experience'
        action={
          <button
            type='button'
            className='btn cta-text-btn'
            onClick={handleAdd}
            disabled={isEditorOpen}
          >
            + Add position
          </button>
        }
      />

      {fields
        .slice(0, savedCount)
        .map((field, index) =>
          index === editingIndex ? null : (
            <ProfessionalExperienceSummary
              key={field.id}
              index={index}
              disabled={isEditorOpen}
              onEdit={() => handleEdit(index)}
              onRemove={() => handleRemove(index)}
            />
          ),
        )}

      {isEditorOpen && (
        <ProfessionalExperienceEditor
          index={editingIndex}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}

function ProfessionalExperienceSummary({
  index,
  onEdit,
  onRemove,
  disabled,
  itemData,
  mode = 'edit',
}: { index: any; onEdit: any; onRemove: any; disabled: any; itemData?: any; mode?: string }) {
  const { getValues } = useFormContext();
  const data = useMemo(
    () => (mode === 'edit' ? getValues(`professionalExperience.${index}`) || {} : itemData || {}),
    [mode, index, getValues, itemData],
  );

  // get month from date
  const resolvedStartMonth = new Date(data.startMonth).getMonth() + 1;
  const resolvedEndMonth = new Date(data.endMonth).getMonth() + 1;
  const resolvedStartYear = new Date(data.startYear).getFullYear();
  const resolvedEndYear = new Date(data.endYear).getFullYear();

  const duration = useMemo(
    () =>
      formatExperienceDuration({
        startMonth: resolvedStartMonth,
        startYear: resolvedStartYear,

        endMonth: resolvedEndMonth,
        endYear: resolvedEndYear,

        isCurrent: data.isCurrent,
      }),
    [data.isCurrent, resolvedEndMonth, resolvedEndYear, resolvedStartMonth, resolvedStartYear],
  );

  const startDateString = `${MONTHS[resolvedStartMonth]} ${resolvedStartYear}`;
  const endDateString = data.isCurrent
    ? 'Present'
    : `${MONTHS[resolvedEndMonth]} ${resolvedEndYear}`;

  return (
    <Stack direction='row' gap={2} sx={{ position: 'relative' }}>
      <Icon icon='mdi:company' width={24} className='text--secondary' />

      <div className='column gap-1'>
        <span>{data.title}</span>
        <div className='row'>
          <span>{data.company}</span>
          <DotSeparator />
          <span>{EMPLOYMENT_TYPES[data.employmentType] || data.employmentType}</span>
        </div>
        <div className='row'>
          <span>
            {startDateString} - {endDateString}
          </span>
          <span>({duration})</span>
        </div>
        <div className='row'>
          <span>{data.location}</span>
          <DotSeparator />
          <span>{WORK_TYPES[data.locationType]}</span>
        </div>
      </div>

      {mode === 'edit' && (
        <Stack direction='row' gap={2} sx={{ position: 'absolute', top: 4, right: 4 }}>
          <IconButton onClick={onEdit} size='small' disabled={disabled}>
            <Icon icon='material-symbols:edit' />
          </IconButton>
          <IconButton onClick={onRemove} size='small' disabled={disabled}>
            <Icon icon='material-symbols:delete' />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}

function ProfessionalExperienceEditor({ index, onSave, onCancel }) {
  const { trigger, setValue } = useFormContext();
  const path = `professionalExperience.${index}`;

  const isCurrent = useWatch({ name: `${path}.isCurrent` });

  useEffect(() => {
    if (isCurrent) {
      setValue(`${path}.endMonth`, '');
      setValue(`${path}.endYear`, '');
    }
  }, [isCurrent, path, setValue]);

  const handleSave = async () => {
    const valid = await trigger(path, { shouldFocus: true });
    if (valid) onSave();
  };

  return (
    <div className='column'>
      <div className='grid grid--2-cols grid--with-divider'>
        <div className='column'>
          <ControlledTextField
            name={`${path}.title`}
            label='Title*'
            rules={{
              required: 'Employment title is required',
              maxLength: EXPERIENCE_LIMITS.TITLE_MAX,
            }}
          />
          <ControlledTextField
            name={`${path}.company`}
            label='Company name*'
            rules={{
              required: 'Company name is required',
              maxLength: EXPERIENCE_LIMITS.COMPANY_MAX,
            }}
          />
          <ControlledSelect
            name={`${path}.locationType`}
            label='Location type*'
            options={WORK_TYPE_OPTIONS}
            rules={{ required: 'Location type is required' }}
          />
        </div>

        <div className='column'>
          <ControlledSelect
            name={`${path}.employmentType`}
            label='Employment type*'
            options={EMPLOYMENT_TYPE_OPTIONS}
            rules={{ required: 'Employment type is required' }}
          />
          <ControlledTextField
            name={`${path}.location`}
            label='Location*'
            rules={{
              required: 'Location is required',
              maxLength: EXPERIENCE_LIMITS.LOCATION_MAX,
            }}
          />
          <ControlledCheckbox name={`${path}.isCurrent`} label='I am currently working here' />
        </div>
      </div>

      <div className='grid grid--2-cols grid--with-divider'>
        <MonthYearField
          label='Start date*'
          monthName={`${path}.startMonth`}
          yearName={`${path}.startYear`}
          required
          variant={MONTH_YEAR_VARIANT.START}
        />

        <MonthYearField
          label='End date*'
          monthName={`${path}.endMonth`}
          yearName={`${path}.endYear`}
          startMonthName={`${path}.startMonth`}
          startYearName={`${path}.startYear`}
          isCurrent={isCurrent}
          required={!isCurrent}
          variant={MONTH_YEAR_VARIANT.END}
        />
      </div>

      <ControlledTextField
        name={`${path}.description`}
        label='Description*'
        isMultiline
        rows={4}
        rules={{
          required: 'Description is required',
          maxLength: {
            value: 1000,
            message: 'Description cannot exceed 1000 characters',
          },
        }}
        showCharCount
      />

      <div className='row row--end gap-3'>
        <button type='button' className='btn outlined-btn' onClick={onCancel}>
          Cancel
        </button>
        <button type='button' className='btn cta-outlined-btn' onClick={handleSave}>
          Save this position
        </button>
      </div>
    </div>
  );
}

function MonthYearField({
  label,
  variant, // 'start' | 'end'
  monthName,
  yearName,
  startMonthName,
  startYearName,
  isCurrent = false,
  disabled = false,
  required = false,
}: { label: any; variant: any; monthName: any; yearName: any; startMonthName?: any; startYearName?: any; isCurrent?: boolean; disabled?: boolean; required?: boolean }) {
  const { getValues } = useFormContext();

  const isStart = variant === MONTH_YEAR_VARIANT.START;
  const isEnd = variant === MONTH_YEAR_VARIANT.END;

  return (
    <div className='column gap-1'>
      <span className='label'>{label}</span>

      <div className='row gap-2'>
        <ControlledDatePicker
          name={monthName}
          views={['month']}
          disabled={disabled || isCurrent}
          format='MMMM'
          rules={{
            validate: () => {
              if (disabled || isCurrent) return true;

              const month = getValues(monthName);
              const year = getValues(yearName);

              if (required && !month) {
                return isStart
                  ? DATE_ERROR_MESSAGES.START_MONTH_REQUIRED
                  : DATE_ERROR_MESSAGES.END_MONTH_REQUIRED;
              }

              if (month && year && isFutureMonthYear(month, year)) {
                return isStart
                  ? DATE_ERROR_MESSAGES.START_IN_FUTURE
                  : DATE_ERROR_MESSAGES.END_IN_FUTURE;
              }

              if (isEnd) {
                const startMonth = getValues(startMonthName);
                const startYear = getValues(startYearName);

                if (
                  startMonth &&
                  startYear &&
                  month &&
                  year &&
                  !isEndAfterStart({
                    startMonth,
                    startYear,
                    endMonth: month,
                    endYear: year,
                  })
                ) {
                  return DATE_ERROR_MESSAGES.END_BEFORE_START;
                }
              }

              return true;
            },
          }}
          wrapperSx={{ maxWidth: 240 }}
          onChangeTransform={(val) => (val ? val.month() + 1 : null)}
          reserveErrorSpace
          slotProps={{ calendarHeader: { sx: { display: 'none' } } }}
        />

        <ControlledDatePicker
          name={yearName}
          views={['year']}
          format='YYYY'
          disabled={disabled || (isEnd && isCurrent)}
          disableFuture
          maxDate={dayjs()}
          slotProps={{ calendarHeader: { sx: { display: 'none' } } }}
          rules={{
            validate: () => {
              if (disabled || (isEnd && isCurrent)) return true;

              const month = getValues(monthName);
              const year = getValues(yearName);

              if (required && !year) {
                return isStart
                  ? DATE_ERROR_MESSAGES.START_YEAR_REQUIRED
                  : DATE_ERROR_MESSAGES.END_YEAR_REQUIRED;
              }

              if (month && year && isFutureMonthYear(month, year)) {
                return isStart
                  ? DATE_ERROR_MESSAGES.START_IN_FUTURE
                  : DATE_ERROR_MESSAGES.END_IN_FUTURE;
              }

              // Highlight year on range error, but message shown on month
              if (isEnd) {
                const startMonth = getValues(startMonthName);
                const startYear = getValues(startYearName);

                if (
                  startMonth &&
                  startYear &&
                  month &&
                  year &&
                  !isEndAfterStart({
                    startMonth,
                    startYear,
                    endMonth: month,
                    endYear: year,
                  })
                ) {
                  return '';
                }
              }

              return true;
            },
          }}
          sx={{ maxWidth: 240 }}
          onChangeTransform={(val) => (val ? val.year() : null)}
          reserveErrorSpace
        />
      </div>
    </div>
  );
}

function DotSeparator() {
  return (
    <Box
      aria-hidden
      component='span'
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        margin: '0 6px',
        opacity: 0.6,
      }}
    />
  );
}
